import {
  ROOT_PITCH_CLASS,
  QUALITY_INTERVALS,
  pitchClassToName,
  splitChordId,
} from "./theory-helpers";

export type ChordQualityKey =
  | "major"
  | "minor"
  | "diminished"
  | "augmented"
  | "sus2"
  | "sus4"
  | "7"
  | "maj7"
  | "m7"
  | "m7b5"
  | "dim7";

export type ChordCategory =
  | "major"
  | "minor"
  | "seventh"
  | "sus"
  | "diminished"
  | "augmented";

export type ChordEntry = {
  id: string;
  root: string;
  quality: ChordQualityKey;
  symbol: string;
  noteNames: string[];
  intervals: number[];
  pitchClasses: number[];
  category: ChordCategory;
};

export type ChordInversion = {
  index: number;
  label: string;
  shortLabel: string;
  midis: number[];
  pitchClasses: number[];
  bassName: string;
};

const QUALITY_TO_SUFFIX: Record<ChordQualityKey, string> = {
  major: "",
  minor: "m",
  diminished: "dim",
  augmented: "aug",
  sus2: "sus2",
  sus4: "sus4",
  "7": "7",
  maj7: "maj7",
  m7: "m7",
  m7b5: "m7b5",
  dim7: "dim7",
};

const QUALITY_TO_CATEGORY: Record<ChordQualityKey, ChordCategory> = {
  major: "major",
  minor: "minor",
  diminished: "diminished",
  augmented: "augmented",
  sus2: "sus",
  sus4: "sus",
  "7": "seventh",
  maj7: "seventh",
  m7: "seventh",
  m7b5: "diminished",
  dim7: "diminished",
};

export const QUALITY_LABEL: Record<ChordQualityKey, string> = {
  major: "Majeur",
  minor: "Mineur",
  diminished: "Diminué",
  augmented: "Augmenté",
  sus2: "Suspendu 2",
  sus4: "Suspendu 4",
  "7": "Septième de dominante",
  maj7: "Septième majeure",
  m7: "Mineure 7",
  m7b5: "Demi-diminuée",
  dim7: "Diminuée 7",
};

export const CATEGORY_HUE: Record<ChordCategory, number> = {
  major: 145,
  minor: 260,
  seventh: 215,
  sus: 185,
  diminished: 0,
  augmented: 30,
};

/** Convert HSL to a Pixi hex color (0xRRGGBB). */
export function hslHex(h: number, s = 75, l = 65): number {
  const hh = ((h % 360) + 360) % 360;
  const ss = s / 100;
  const ll = l / 100;
  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ll - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 60) {
    [r, g, b] = [c, x, 0];
  } else if (hh < 120) {
    [r, g, b] = [x, c, 0];
  } else if (hh < 180) {
    [r, g, b] = [0, c, x];
  } else if (hh < 240) {
    [r, g, b] = [0, x, c];
  } else if (hh < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }

  const ri = Math.round((r + m) * 255);
  const gi = Math.round((g + m) * 255);
  const bi = Math.round((b + m) * 255);

  return (ri << 16) | (gi << 8) | bi;
}

export const CATEGORY_LABEL: Record<ChordCategory, string> = {
  major: "Majeurs",
  minor: "Mineurs",
  seventh: "Septièmes",
  sus: "Suspendus",
  diminished: "Diminués",
  augmented: "Augmentés",
};

export const CATEGORY_ORDER: ChordCategory[] = [
  "major",
  "minor",
  "seventh",
  "sus",
  "diminished",
  "augmented",
];

/** Canonical roots for catalog entries, practical Western spellings: flats
 *  for Bb/Eb/Ab, sharps for C#/F#. `entryById` normalizes any enharmonic
 *  input (e.g. "A#" → "Bb", "Db" → "C#") so callers never have to care. */
export const ROOTS_CANONICAL: string[] = [
  "C",
  "C#",
  "D",
  "Eb",
  "E",
  "F",
  "F#",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];

const INVERSION_LABELS = [
  "Position fondamentale",
  "1er renversement",
  "2e renversement",
  "3e renversement",
];

const INVERSION_SHORT = ["Fond.", "1er", "2e", "3e"];

function buildEntry(root: string, quality: ChordQualityKey): ChordEntry {
  const rootPc = ROOT_PITCH_CLASS[root];
  const intervals = QUALITY_INTERVALS[quality];
  const symbol = root + QUALITY_TO_SUFFIX[quality];
  const pitchClasses = intervals.map((iv) => (rootPc + iv) % 12);
  const noteNames = pitchClasses.map((pc) => pitchClassToName(pc, root));

  return {
    id: symbol,
    root,
    quality,
    symbol,
    noteNames,
    intervals,
    pitchClasses,
    category: QUALITY_TO_CATEGORY[quality],
  };
}

const ESSENTIAL_QUALITIES: ChordQualityKey[] = ["major", "minor"];

const FULL_QUALITIES: ChordQualityKey[] = [
  "major",
  "minor",
  "7",
  "maj7",
  "m7",
  "diminished",
  "augmented",
  "sus2",
  "sus4",
  "m7b5",
  "dim7",
];

export const ESSENTIAL_CATALOG: ChordEntry[] = ROOTS_CANONICAL.flatMap((r) =>
  ESSENTIAL_QUALITIES.map((q) => buildEntry(r, q)),
);

export const FULL_CATALOG: ChordEntry[] = ROOTS_CANONICAL.flatMap((r) =>
  FULL_QUALITIES.map((q) => buildEntry(r, q)),
);

/** Maps any 1- or 2-char chord root (sharp- or flat-spelled) to its canonical
 *  spelling used by the catalog. Returns null when the root isn't recognised. */
function canonicalRootOf(root: string): string | null {
  const pc = ROOT_PITCH_CLASS[root];

  if (pc === undefined) {
    return null;
  }

  return ROOTS_CANONICAL[pc] ?? null;
}

export function entryById(id: string, full = true): ChordEntry | null {
  const source = full ? FULL_CATALOG : ESSENTIAL_CATALOG;

  const direct = source.find((e) => e.id === id);

  if (direct) {
    return direct;
  }

  const parts = splitChordId(id);

  if (!parts) {
    return null;
  }

  const canonicalRoot = canonicalRootOf(parts.root);

  if (!canonicalRoot) {
    return null;
  }

  const canonicalId = canonicalRoot + parts.suffix;

  return source.find((e) => e.id === canonicalId) ?? null;
}

/** Returns all entries sharing the same root, in canonical quality order. */
export function entrySiblings(entry: ChordEntry): ChordEntry[] {
  return FULL_CATALOG.filter((e) => e.root === entry.root);
}

function nearestMidiWithPc(pc: number, reference: number): number {
  const refPc = ((reference % 12) + 12) % 12;
  let delta = (((pc - refPc) % 12) + 12) % 12;
  if (delta > 6) {
    delta -= 12;
  }

  return reference + delta;
}

export function entryInversions(
  entry: ChordEntry,
  anchorMidi = 60,
): ChordInversion[] {
  const n = entry.intervals.length;
  const result: ChordInversion[] = [];

  for (let inv = 0; inv < n; inv++) {
    const rotatedPcs: number[] = [];
    for (let k = 0; k < n; k++) {
      const idx = (k + inv) % n;
      rotatedPcs.push(entry.pitchClasses[idx]);
    }

    const midis: number[] = [];
    let cursor = nearestMidiWithPc(rotatedPcs[0], anchorMidi);
    midis.push(cursor);
    for (let k = 1; k < n; k++) {
      let m = cursor + 1;
      while (m % 12 !== rotatedPcs[k]) {
        m++;
      }

      midis.push(m);
      cursor = m;
    }

    result.push({
      index: inv,
      label: INVERSION_LABELS[inv] ?? `Renversement ${inv}`,
      shortLabel: INVERSION_SHORT[inv] ?? String(inv),
      midis,
      pitchClasses: rotatedPcs,
      bassName: pitchClassToName(rotatedPcs[0], entry.root),
    });
  }

  return result;
}

const TONE_LABEL_FR: Record<string, string> = {
  root: "fondamentale",
  "3rd": "tierce majeure",
  "b3rd": "tierce mineure",
  "5th": "quinte juste",
  b5: "quinte diminuée",
  b13: "quinte augmentée",
  "13th": "sixte",
  "7th": "septième majeure",
  "b7th": "septième mineure",
  "9th": "neuvième",
  "11th": "onzième",
};

const INTERVAL_TO_TONE: Record<number, string> = {
  0: "root",
  2: "9th",
  3: "b3rd",
  4: "3rd",
  5: "11th",
  6: "b5",
  7: "5th",
  8: "b13",
  9: "13th",
  10: "b7th",
  11: "7th",
};

export type PracticePosition = {
  /** Stable id, e.g. "Em-inv0-oct3". */
  id: string;
  inversionIndex: number;
  inversionLabel: string;
  inversionShort: string;
  /** Bass octave in scientific pitch notation (C0=12, C1=24, …). */
  bassOctave: number;
  midis: number[];
  pitchClasses: number[];
  bassName: string;
};

const PIANO_MIN_MIDI = 21;
const PIANO_MAX_MIDI = 108;

/**
 * Build all practice positions for an entry, structured as rows × cols.
 * Rows are inversions, cols are octaves (provided by `octaves` arg).
 * Out-of-keyboard positions are returned as `null` so the grid stays aligned.
 */
export function entryPracticePositions(
  entry: ChordEntry,
  octaves: number[] = [2, 3, 4],
): (PracticePosition | null)[][] {
  const n = entry.intervals.length;
  const rows: (PracticePosition | null)[][] = [];

  for (let inv = 0; inv < n; inv++) {
    const rotatedPcs: number[] = [];
    for (let k = 0; k < n; k++) {
      const idx = (k + inv) % n;
      rotatedPcs.push(entry.pitchClasses[idx]);
    }

    const row: (PracticePosition | null)[] = [];
    for (const oct of octaves) {
      const bassMidi = (oct + 1) * 12 + rotatedPcs[0];
      const midis: number[] = [bassMidi];
      let cursor = bassMidi;
      for (let k = 1; k < n; k++) {
        let m = cursor + 1;
        while (m % 12 !== rotatedPcs[k]) {
          m++;
        }

        midis.push(m);
        cursor = m;
      }

      const inRange = midis.every(
        (m) => m >= PIANO_MIN_MIDI && m <= PIANO_MAX_MIDI,
      );
      if (!inRange) {
        row.push(null);
        continue;
      }

      row.push({
        id: `${entry.id}-inv${inv}-oct${oct}`,
        inversionIndex: inv,
        inversionLabel: INVERSION_LABELS[inv] ?? `Renversement ${inv}`,
        inversionShort: INVERSION_SHORT[inv] ?? String(inv),
        bassOctave: oct,
        midis,
        pitchClasses: rotatedPcs,
        bassName: pitchClassToName(rotatedPcs[0], entry.root),
      });
    }

    rows.push(row);
  }

  return rows;
}

export type ScaleDegree = {
  pc: number;
  name: string;
  /** Roman numeral position in the scale. */
  roman: string;
  /** Whether this scale degree is part of the chord. */
  isChordTone: boolean;
};

export type ScaleMode =
  | "ionian"
  | "dorian"
  | "mixolydian"
  | "aeolian"
  | "locrian"
  | "wholeTone"
  | "wholeHalfDim";

/** Mode intervals from root. Some modes have non-7 lengths (whole-tone=6, dim=8). */
const MODE_INTERVALS: Record<ScaleMode, number[]> = {
  ionian: [0, 2, 4, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  wholeTone: [0, 2, 4, 6, 8, 10],
  wholeHalfDim: [0, 2, 3, 5, 6, 8, 9, 11],
};

/** Quality → most natural parent scale (the one that contains the chord tones). */
const QUALITY_TO_MODE: Record<ChordQualityKey, ScaleMode> = {
  major: "ionian",
  maj7: "ionian",
  sus2: "ionian",
  sus4: "ionian",
  minor: "aeolian",
  m7: "dorian",
  "7": "mixolydian",
  diminished: "locrian",
  m7b5: "locrian",
  augmented: "wholeTone",
  dim7: "wholeHalfDim",
};

const ROMANS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

/**
 * Returns the parent scale most natural to the chord (contains all chord tones).
 * Each degree is annotated with whether it's part of the chord. Length varies:
 * 6 (whole-tone for augmented), 7 (most modes), 8 (whole-half for dim7).
 */
export function entryScale(entry: ChordEntry): ScaleDegree[] {
  const mode = QUALITY_TO_MODE[entry.quality] ?? "ionian";
  const intervals = MODE_INTERVALS[mode];
  const rootPc = ROOT_PITCH_CLASS[entry.root];
  const chordSet = new Set(entry.pitchClasses);

  return intervals.map((iv, i) => {
    const pc = (rootPc + iv) % 12;

    return {
      pc,
      name: pitchClassToName(pc, entry.root),
      roman: ROMANS[i] ?? `${i + 1}`,
      isChordTone: chordSet.has(pc),
    };
  });
}

/** Returns the scale mode used by `entryScale` for this entry's quality. */
export function entryScaleMode(entry: ChordEntry): ScaleMode {
  return QUALITY_TO_MODE[entry.quality] ?? "ionian";
}

// ── Diatonic chords ─────────────────────────────────────────────

const DIATONIC_QUALITIES: Partial<Record<ScaleMode, ChordQualityKey[]>> = {
  ionian: ["major", "minor", "minor", "major", "major", "minor", "diminished"],
  aeolian: ["minor", "diminished", "major", "minor", "minor", "major", "major"],
  dorian: ["minor", "minor", "major", "major", "minor", "diminished", "major"],
  mixolydian: ["major", "minor", "diminished", "major", "minor", "minor", "major"],
  locrian: ["diminished", "major", "minor", "minor", "major", "major", "minor"],
};

const DIATONIC_ROMANS: Partial<Record<ScaleMode, string[]>> = {
  ionian: ["I", "ii", "iii", "IV", "V", "vi", "vii°"],
  aeolian: ["i", "ii°", "♭III", "iv", "v", "♭VI", "♭VII"],
  dorian: ["i", "ii", "♭III", "IV", "v", "vi°", "♭VII"],
  mixolydian: ["I", "ii", "iii°", "IV", "v", "vi", "♭VII"],
  locrian: ["i°", "♭II", "iii", "iv", "♭V", "♭VI", "♭vii"],
};

export type DiatonicChord = {
  entry: ChordEntry;
  roman: string;
  isCurrent: boolean;
};

/**
 * Returns the diatonic chords (triads on each scale degree) of a key's parent
 * scale. Major keys → ionian; minor keys → aeolian. `currentEntryId` flags
 * which diatonic chord is currently being practiced.
 */
export function diatonicChordsForKey(
  tonic: string,
  keyMode: "major" | "minor",
  currentEntryId?: string,
): DiatonicChord[] {
  const rootPc = ROOT_PITCH_CLASS[tonic];

  if (rootPc === undefined) {
    return [];
  }

  const mode: ScaleMode = keyMode === "major" ? "ionian" : "aeolian";
  const intervals = MODE_INTERVALS[mode];
  const qualities = DIATONIC_QUALITIES[mode];
  const romans = DIATONIC_ROMANS[mode];

  if (!qualities || !romans) {
    return [];
  }

  const out: DiatonicChord[] = [];

  for (let i = 0; i < Math.min(intervals.length, qualities.length); i++) {
    const pc = (rootPc + intervals[i]) % 12;
    const sharpRoot = pitchClassToName(pc, "C");
    const id = sharpRoot + QUALITY_TO_SUFFIX[qualities[i]];
    const found = entryById(id);

    if (!found) {
      continue;
    }

    out.push({
      entry: found,
      roman: romans[i],
      isCurrent: currentEntryId ? found.id === currentEntryId : false,
    });
  }

  return out;
}

/**
 * Returns the diatonic chords (triads on each scale degree) of the entry's
 * parent scale. Includes a Roman-numeral label per degree. For modes without
 * a defined diatonic pattern (whole-tone, diminished), returns an empty array.
 */
export function entryDiatonicChords(entry: ChordEntry): DiatonicChord[] {
  const mode = entryScaleMode(entry);
  const qualities = DIATONIC_QUALITIES[mode];
  const romans = DIATONIC_ROMANS[mode];
  if (!qualities || !romans) {
    return [];
  }

  const scale = entryScale(entry);
  const out: DiatonicChord[] = [];
  for (let i = 0; i < Math.min(scale.length, qualities.length); i++) {
    // Catalog uses sharp roots, derive the canonical sharp name from PC.
    const sharpRoot = pitchClassToName(scale[i].pc, "C");
    const id = sharpRoot + QUALITY_TO_SUFFIX[qualities[i]];
    const found = entryById(id);
    if (!found) {
      continue;
    }

    out.push({
      entry: found,
      roman: romans[i],
      isCurrent: found.id === entry.id,
    });
  }

  return out;
}

export type ChordToneInfo = {
  pc: number;
  name: string;
  interval: number;
  toneKey: string;
  toneLabel: string;
};

// ── V7d practice helpers ───────────────────────────────────────

/** Apply inversion N: move first N notes up an octave, then sort. */
export function applyInversion(intervals: number[], invIdx: number): number[] {
  const out = intervals.slice();
  for (let i = 0; i < invIdx; i++) {
    out[i] += 12;
  }

  out.sort((a, b) => a - b);

  return out;
}

/** Rotate a chord-tone label array by N (matches inversion semantics). */
export function rotateDegrees(degrees: string[], invIdx: number): string[] {
  return [...degrees.slice(invIdx), ...degrees.slice(0, invIdx)];
}

/** French interval name from the number of semitones (1..12). */
export function intervalNameFR(semitones: number): string {
  switch (semitones) {
    case 1:
      return "seconde min.";
    case 2:
      return "seconde maj.";
    case 3:
      return "tierce min.";
    case 4:
      return "tierce maj.";
    case 5:
      return "quarte juste";
    case 6:
      return "triton";
    case 7:
      return "quinte juste";
    case 8:
      return "sixte min.";
    case 9:
      return "sixte maj.";
    case 10:
      return "septième min.";
    case 11:
      return "septième maj.";
    case 12:
      return "octave juste";
    default:
      return `${semitones} demi-tons`;
  }
}

/** Display label per chord-tone interval (matching V7d formula notation). */
const INTERVAL_LABEL: Record<number, string> = {
  0: "1",
  1: "♭2",
  2: "2",
  3: "♭3",
  4: "3",
  5: "4",
  6: "♭5",
  7: "5",
  8: "♯5",
  9: "6",
  10: "♭7",
  11: "7",
};

export function intervalDegreeLabel(semitones: number): string {
  return INTERVAL_LABEL[((semitones % 12) + 12) % 12] ?? `${semitones}`;
}

export type FunctionalRole = {
  /** Roman numeral with quality marks, e.g. "I", "iv", "vii°", "V7", "Imaj7". */
  degree: string;
  /** Sharp-spelled tonic name, e.g. "C", "F#". */
  tonic: string;
  /** Major or minor key. */
  mode: "major" | "minor";
};

/** Returns the most common functional roles the chord plays across keys. */
export function entryFunctionalContext(entry: ChordEntry): FunctionalRole[] {
  const rootPc = ROOT_PITCH_CLASS[entry.root];
  const tonicPc = (semitonesBelow: number) =>
    ((rootPc - semitonesBelow + 1200) % 12);
  const tonicName = (semitonesBelow: number) =>
    pitchClassToName(tonicPc(semitonesBelow), "C");

  switch (entry.quality) {
    case "major":
      return [
        { degree: "I", tonic: tonicName(0), mode: "major" },
        { degree: "IV", tonic: tonicName(5), mode: "major" },
        { degree: "V", tonic: tonicName(7), mode: "major" },
      ];
    case "minor":
      return [
        { degree: "i", tonic: tonicName(0), mode: "minor" },
        { degree: "ii", tonic: tonicName(2), mode: "major" },
        { degree: "vi", tonic: tonicName(9), mode: "major" },
      ];
    case "diminished":
      return [
        { degree: "vii°", tonic: tonicName(11), mode: "major" },
        { degree: "ii°", tonic: tonicName(2), mode: "minor" },
      ];
    case "augmented":
      return [];
    case "7":
      return [
        { degree: "V7", tonic: tonicName(7), mode: "major" },
        { degree: "V7", tonic: tonicName(7), mode: "minor" },
      ];
    case "maj7":
      return [
        { degree: "Imaj7", tonic: tonicName(0), mode: "major" },
        { degree: "IVmaj7", tonic: tonicName(5), mode: "major" },
      ];
    case "m7":
      return [
        { degree: "ii7", tonic: tonicName(2), mode: "major" },
        { degree: "vi7", tonic: tonicName(9), mode: "major" },
      ];
    case "sus2":
    case "sus4":
      return [];
    default:
      return [];
  }
}

export function entryTones(entry: ChordEntry): ChordToneInfo[] {
  return entry.intervals.map((iv, i) => {
    const toneKey = INTERVAL_TO_TONE[iv] ?? `+${iv}`;

    return {
      pc: entry.pitchClasses[i],
      name: entry.noteNames[i],
      interval: iv,
      toneKey,
      toneLabel: TONE_LABEL_FR[toneKey] ?? toneKey,
    };
  });
}
